import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import usubleaseLogo from '../../images/green.png';
import SignInPopup from '../SignInPopup';
import GlassCapsulePopup from '../GlassCapsulePopup/GlassCapsulePopup';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import AvatarFallback from '../AvatarFallback/AvatarFallback';
import BoostModal from '../BoostModal/BoostModal';
import { API_BASE_URL } from '../../config/api';
import './NavBar.css';

interface NavBarProps {
    inverted?: boolean;
    centerContent?: React.ReactNode;
}

const NavBar: React.FC<NavBarProps> = ({ inverted = false, centerContent }) => {
    const navbarRef = useRef<HTMLElement | null>(null);
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [isSignInPopupOpen, setIsSignInPopupOpen] = useState(false);
    const [showAuthWarningPopup, setShowAuthWarningPopup] = useState(false);
    const { user, isAuthenticated, token, logout, isLoading } = useAuth();
    const { unreadCount } = useNotifications();
    const location = useLocation();
    const navigate = useNavigate();
    const isSignUpPage = location.pathname === '/signup';
    const isForgotPasswordPage = location.pathname === '/forgot';
    
    // Use has_listings from user object if available, otherwise default to false
    const hasListings = user?.has_listings || false;
    const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);
    const [cachedListings, setCachedListings] = useState<any[]>([]);
    const [listingsLoaded, setListingsLoaded] = useState(false);

    // Hide the boost button if the user has listings and all of them are already boosted
    const hasUnboostedListings = hasListings && (
        !listingsLoaded ||
        cachedListings.length === 0 ||
        cachedListings.some(l => !l.is_boosted)
    );

    // Prefetch user listings as soon as authenticated so Boost modal opens instantly
    React.useEffect(() => {
        if (isAuthenticated && hasListings && token) {
            fetch(`${API_BASE_URL}/api/v1/users/listings`, {
                headers: { 'Authorization': `Bearer ${token}` }
            })
                .then(res => res.ok ? res.json() : null)
                .then(data => {
                    if (data?.data) {
                        setCachedListings(data.data);
                        setListingsLoaded(true);
                    }
                })
                .catch(() => {
                    setListingsLoaded(true); // Still mark as loaded on error to avoid lingering loader state
                });
        }
    }, [isAuthenticated, hasListings, token]);

    React.useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        if (queryParams.get('login') === 'true' && !isAuthenticated && !isLoading) {
            setIsSignInPopupOpen(true);
            // Clear the login param but keep others
            queryParams.delete('login');
            const newSearch = queryParams.toString();
            navigate({
                pathname: location.pathname,
                search: newSearch ? `?${newSearch}` : ''
            }, { replace: true });
        }
    }, [location.search, isAuthenticated, isLoading, navigate, location.pathname]);

    const handleLogout = () => {
        logout();
        setShowUserDropdown(false);
        navigate('/'); // Redirect to home page after logout
    };

    const handleLogoClick = () => {
        // Clear all filters and university search from localStorage
        try {
            localStorage.removeItem('usublease_filters');
        } catch (error) {
            console.error('Error clearing filters:', error);
        }
        // Navigate to home page with no query params to clear filters
        window.location.href = '/';
    };

    const handleNewListingClick = () => {
        if (isAuthenticated) {
            // Navigate to create page if authenticated
            navigate('/create');
        } else {
            // Show auth warning popup if not authenticated
            setShowAuthWarningPopup(true);
        }
    };

    const logoToUse = usubleaseLogo;
    const navbarClass = inverted ? 'navbar navbar-inverted' : 'navbar';

    useLayoutEffect(() => {
        const navbarElement = navbarRef.current;

        if (!navbarElement || typeof window === 'undefined') {
            return;
        }

        const updateNavbarHeight = () => {
            const height = Math.ceil(navbarElement.getBoundingClientRect().height);
            document.documentElement.style.setProperty('--navbar-height', `${height}px`);
        };

        updateNavbarHeight();

        const resizeObserver = new ResizeObserver(updateNavbarHeight);
        resizeObserver.observe(navbarElement);
        window.addEventListener('resize', updateNavbarHeight);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', updateNavbarHeight);
        };
    }, []);

    return (
        <>
            <header ref={navbarRef} className={navbarClass}>
                <div className="navbar-inner">
                    <div className="NAVLINES" aria-hidden="true">
                        <span className="NAVLINE NAVLINE--t" />
                        <span className="NAVLINE NAVLINE--m" />
                        <span className="NAVLINE NAVLINE--b" />
                    </div>
                    <button onClick={handleLogoClick} className="logo">
                        <img src={logoToUse} alt="usublease" className="logo-icon" />
                        <span className="brand">
                            <span className="brand-text">
                                <span className="brand-text-inner">USUBLEASE</span>
                            </span>
                        </span>
                    </button>
                    
                    {centerContent && (
                        <div className="navbar-center-content">
                            {centerContent}
                        </div>
                    )}

                    <div className="navbar-actions">
                        {!isSignUpPage && !isForgotPasswordPage && (
                            <>
                                <button
                                    onClick={handleNewListingClick}
                                    className="create-listing-btn"
                                >
                                    <span className="create-listing-text-full">Create</span>
                                    <span className="create-listing-text-mobile">Create</span>
                                </button>
                                
                                {isAuthenticated && (!user?.student_verification_status || user.student_verification_status === 'unverified' || user.student_verification_status === 'rejected') && (
                                    <button
                                        onClick={() => navigate('/profile?verify=true')}
                                        className="navbar-verify-btn"
                                    >
                                        Get Verified
                                    </button>
                                )}

                                {hasUnboostedListings && (
                                    <button
                                        onClick={() => setIsBoostModalOpen(true)}
                                        className="navbar-boost-btn"
                                    >
                                        <span className="fire-text">Boost</span>
                                    </button>
                                )}
                            </>
                        )}

                        {!isLoading && !isAuthenticated && !isSignUpPage && !isForgotPasswordPage && (
                            <button
                                onClick={() => setIsSignInPopupOpen(true)}
                                className="navbar-auth-link"
                            >
                                Sign in
                            </button>
                        )}

                        {isAuthenticated && (
                            <>
                                <div className="user-dropdown-container">
                                    <button
                                        className="user-avatar-btn"
                                        onClick={() => setShowUserDropdown(!showUserDropdown)}
                                    >
                                        {user?.avatar_url ? (
                                            <img
                                                key={user.avatar_url} // Force re-render when avatar URL changes
                                                src={user.avatar_url}
                                                alt="User avatar"
                                                className="user-avatar"
                                                onError={(e) => {
                                                    e.currentTarget.style.display = 'none';
                                                    const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                                                    if (placeholder) {
                                                        placeholder.style.display = 'flex';
                                                    }
                                                }}
                                            />
                                        ) : null}
                                        <AvatarFallback
                                            firstName={user?.first_name}
                                            lastName={user?.last_name}
                                            email={user?.email}
                                            size={42}
                                            className="user-avatar-placeholder"
                                            style={{ display: user?.avatar_url ? 'none' : 'flex' }}
                                        />
                                        {user?.student_verification_status === 'approved' && (
                                            <div
                                                className="user-avatar-verified-badge"
                                                title="Verified User"
                                             >
                                                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M8.5 12.5L11 15L16 9.5" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </div>
                                        )}
                                        {unreadCount > 0 && (
                                            <div className={`notification-badge ${user?.student_verification_status === 'approved' ? 'has-verification' : ''}`}>
                                                {unreadCount > 99 ? '99+' : unreadCount}
                                            </div>
                                        )}
                                    </button>

                                    {showUserDropdown && (
                                        <div className="user-dropdown">
                                            <Link to="/profile" className="dropdown-item" onClick={() => setShowUserDropdown(false)}>
                                                <span className="dropdown-item-content">
                                                    Profile
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </span>
                                            </Link>
                                            <Link to="/messages" className="dropdown-item" onClick={() => setShowUserDropdown(false)}>
                                                <span className="dropdown-item-content">
                                                    Messages
                                                    <div className="messages-icon-container">
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M22 6L12 13L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                        {unreadCount > 0 && (
                                                            <span className="messages-icon-badge"></span>
                                                        )}
                                                    </div>
                                                </span>
                                            </Link>
                                            <button className="dropdown-item logout-btn" onClick={handleLogout}>
                                                <span className="dropdown-item-content">
                                                    Sign Out
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9M16 17L21 12M21 12L16 7M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </header>
            <SignInPopup
                isOpen={isSignInPopupOpen}
                onClose={() => setIsSignInPopupOpen(false)}
                redirectPath={location.pathname}
            />
            <GlassCapsulePopup
                message="Create an account or sign in to create a listing."
                show={showAuthWarningPopup}
                onClose={() => setShowAuthWarningPopup(false)}
                autoHideDuration={3000}
            />
            {isAuthenticated && (
                <BoostModal 
                    isOpen={isBoostModalOpen} 
                    onClose={() => setIsBoostModalOpen(false)}
                    listings={cachedListings.length > 0 ? cachedListings : undefined}
                />
            )}
        </>
    );
};

export default NavBar;
